import React, { useEffect, useRef, useState } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import { MediaUpload } from "../../../../Constant/api";

export default function ImageUpload(props) {
  const {
    ImageUrl,
    setImageUrl,
    onChange,
    type,
    index
  } = props;

  const fileRefSingle = useRef(null);
  const cropImageRef = useRef(null);

  // Upload
  const [Loading, setLoading] = useState(false);

  // Drag/drop
  const [isDragging, setIsDragging] = useState(false);

  // Selected local file
  const [selectedFile, setSelectedFile] = useState(null);

  // Local blob URL
  const [localImageUrl, setLocalImageUrl] = useState("");

  // Crop modal
  const [showCrop, setShowCrop] = useState(false);

  // Crop values
  const [crop, setCrop] = useState({
    unit: "%",
    x: 10,
    y: 10,
    width: 80,
    height: 80
  });

  const [completedCrop, setCompletedCrop] = useState(null);

  /*
   * Clean blob URL when localImageUrl changes
   * or component is destroyed
   */
  useEffect(() => {
    return () => {
      if (localImageUrl) {
        URL.revokeObjectURL(localImageUrl);
      }
    };
  }, [localImageUrl]);

  /*
   * Your original filesize function.
   *
   * If your server DOES NOT require a minimum
   * 2MB file size, you can remove this function.
   */
  function increaseFileSize(file, targetSizeMB) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const data = new Uint8Array(reader.result);

          const targetSize =
            targetSizeMB * 1024 * 1024;

          if (data.length < targetSize) {
            const padding =
              new Uint8Array(
                targetSize - data.length
              );

            const combined =
              new Uint8Array(
                data.length + padding.length
              );

            combined.set(data);
            combined.set(
              padding,
              data.length
            );

            const newFile = new File(
              [combined],
              file.name,
              {
                type: file.type
              }
            );

            resolve(newFile);
          } else {
            resolve(file);
          }
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = reject;

      reader.readAsArrayBuffer(file);
    });
  }

  /*
   * Prepare selected local image
   *
   * IMPORTANT:
   * Nothing is uploaded here.
   */
  const prepareImageForCrop = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }

    // Remove previous object URL
    if (localImageUrl) {
      URL.revokeObjectURL(localImageUrl);
    }

    const url =
      URL.createObjectURL(file);

    setSelectedFile(file);

    setLocalImageUrl(url);

    // Reset crop
    setCrop({
      unit: "%",
      x: 10,
      y: 10,
      width: 80,
      height: 80
    });

    setCompletedCrop(null);

    // Open crop popup
    setShowCrop(true);
  };

  /*
   * Normal file picker
   */
  const handleFileSelect = (event) => {
    const file =
      event.target.files?.[0];

    if (!file) return;

    prepareImageForCrop(file);
  };

  /*
   * Drag enter
   */
  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(true);
  };

  /*
   * Drag over
   */
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(true);
  };

  /*
   * Drag leave
   */
  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(false);
  };

  /*
   * Drop image
   */
  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(false);

    const file =
      event.dataTransfer.files?.[0];

    if (!file) return;

    prepareImageForCrop(file);
  };

  /*
   * Convert crop selection into actual File
   */
  const getCroppedFile = (
    image,
    cropValue,
    originalFile
  ) => {
    return new Promise((resolve, reject) => {
      if (
        !image ||
        !cropValue ||
        !cropValue.width ||
        !cropValue.height
      ) {
        reject(
          new Error("Invalid crop area.")
        );

        return;
      }

      const canvas =
        document.createElement("canvas");

      const ctx =
        canvas.getContext("2d");

      if (!ctx) {
        reject(
          new Error(
            "Canvas is not supported."
          )
        );

        return;
      }

      /*
       * Displayed image may be smaller than
       * actual image.
       *
       * Therefore calculate scale.
       */
      const scaleX =
        image.naturalWidth /
        image.width;

      const scaleY =
        image.naturalHeight /
        image.height;

      const cropX =
        cropValue.x * scaleX;

      const cropY =
        cropValue.y * scaleY;

      const cropWidth =
        cropValue.width * scaleX;

      const cropHeight =
        cropValue.height * scaleY;

      canvas.width =
        Math.round(cropWidth);

      canvas.height =
        Math.round(cropHeight);

      /*
       * Draw selected section
       */
      ctx.drawImage(
        image,

        cropX,
        cropY,
        cropWidth,
        cropHeight,

        0,
        0,
        canvas.width,
        canvas.height
      );

      /*
       * Keep PNG if original is PNG,
       * otherwise use JPEG.
       */
      let outputType = "image/jpeg";
      let extension = "jpg";

      if (
        originalFile?.type ===
        "image/png"
      ) {
        outputType = "image/png";
        extension = "png";
      }

      if (
        originalFile?.type ===
        "image/webp"
      ) {
        outputType = "image/webp";
        extension = "webp";
      }

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(
              new Error(
                "Failed to create cropped image."
              )
            );

            return;
          }

          const fileName =
            `cropped-${Date.now()}.${extension}`;

          const croppedFile =
            new File(
              [blob],
              fileName,
              {
                type: outputType
              }
            );

          resolve(croppedFile);
        },

        outputType,

        /*
         * JPEG / WebP quality
         */
        0.92
      );
    });
  };

  /*
   * Crop local image
   * THEN upload cropped file.
   */
  const uploadCroppedImage = async () => {
    if (!selectedFile) {
      alert("No image selected.");
      return;
    }

    if (!cropImageRef.current) {
      alert("Image is not ready.");
      return;
    }

    if (
      !completedCrop ||
      !completedCrop.width ||
      !completedCrop.height
    ) {
      alert(
        "Please select a crop area."
      );

      return;
    }

    try {
      setLoading(true);

      /*
       * STEP 1
       * Crop local image
       */
      const croppedFile =
        await getCroppedFile(
          cropImageRef.current,
          completedCrop,
          selectedFile
        );

      console.log(
        "Cropped File:",
        croppedFile
      );

      /*
       * STEP 2
       *
       * Keep this only if your server
       * really needs minimum 2MB.
       */
      const uploadFile =
        await increaseFileSize(
          croppedFile,
          2
        );

      /*
       * STEP 3
       * Upload cropped file
       */
      const res =
        await MediaUpload(
          uploadFile
        );

      /*
       * Optional error check
       */
      if (!res.ok) {
        throw new Error(
          "Server upload failed."
        );
      }

      const data =
        await res.json();

      if (!data?.source_url) {
        throw new Error(
          "source_url was not returned."
        );
      }

      /*
       * STEP 4
       * Save URL returned from server
       */
      setImageUrl(
        data.source_url
      );

      /*
       * STEP 5
       * Update parent component
       */
      onChange(
        type,
        data.source_url,
        index
      );

      /*
       * STEP 6
       * Close crop popup
       */
      setShowCrop(false);

      /*
       * Remove local temporary image
       */
      if (localImageUrl) {
        URL.revokeObjectURL(
          localImageUrl
        );
      }

      setLocalImageUrl("");
      setSelectedFile(null);
      setCompletedCrop(null);

      /*
       * Reset input
       */
      if (fileRefSingle.current) {
        fileRefSingle.current.value = "";
      }

    } catch (error) {
      console.error(
        "Crop / Upload Error:",
        error
      );

      alert(
        error.message ||
        "Unable to crop and upload image."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * Upload original image WITHOUT crop
   *
   * Optional button.
   */
  const uploadOriginalImage = async () => {
    if (!selectedFile) {
      return;
    }

    try {
      setLoading(true);

      const uploadFile =
        await increaseFileSize(
          selectedFile,
          2
        );

      const res =
        await MediaUpload(
          uploadFile
        );

      if (!res.ok) {
        throw new Error(
          "Server upload failed."
        );
      }

      const data =
        await res.json();

      if (!data?.source_url) {
        throw new Error(
          "source_url was not returned."
        );
      }

      setImageUrl(
        data.source_url
      );

      onChange(
        type,
        data.source_url,
        index
      );

      setShowCrop(false);

      if (localImageUrl) {
        URL.revokeObjectURL(
          localImageUrl
        );
      }

      setLocalImageUrl("");
      setSelectedFile(null);
      setCompletedCrop(null);

      if (fileRefSingle.current) {
        fileRefSingle.current.value = "";
      }

    } catch (error) {
      console.error(
        "Upload Error:",
        error
      );

      alert(
        error.message ||
        "Image upload failed."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * Close crop modal
   */
  const cancelCrop = () => {
    setShowCrop(false);

    if (localImageUrl) {
      URL.revokeObjectURL(
        localImageUrl
      );
    }

    setLocalImageUrl("");
    setSelectedFile(null);
    setCompletedCrop(null);

    if (fileRefSingle.current) {
      fileRefSingle.current.value = "";
    }
  };

  /*
   * Remove already uploaded image
   */
  const handleClear = () => {
    setImageUrl("");

    onChange(
      type,
      "",
      index
    );

    if (fileRefSingle.current) {
      fileRefSingle.current.value = "";
    }
  };

  return (
    <div>

      {/* ================================= */}
      {/* LOADING */}
      {/* ================================= */}

      {Loading && (
        <div className="mb-2">
          <div
            className="
              spinner-border
              spinner-border-sm
            "
            role="status"
          />

          <span className="ms-2">
            Uploading...
          </span>
        </div>
      )}


      {/* ================================= */}
      {/* NO IMAGE */}
      {/* ================================= */}

      {!ImageUrl && (
        <div
          onDragEnter={
            handleDragEnter
          }

          onDragOver={
            handleDragOver
          }

          onDragLeave={
            handleDragLeave
          }

          onDrop={
            handleDrop
          }

          onClick={() => {
            if (!Loading) {
              fileRefSingle.current?.click();
            }
          }}

          style={{
            border:
              isDragging
                ? "2px dashed #0d6efd"
                : "2px dashed #aaa",

            background:
              isDragging
                ? "#f0f7ff"
                : "#ffffff",

            borderRadius:
              "8px",

            padding:
              "25px",

            textAlign:
              "center",

            cursor:
              Loading
                ? "not-allowed"
                : "pointer",

            transition:
              "all 0.2s ease"
          }}
        >

          <i
            className="
              fa
              fa-cloud-upload
            "
            style={{
              fontSize: "35px"
            }}
          />

          <div
            style={{
              fontSize: "14px",
              fontWeight: 500,
              marginTop: "5px"
            }}
          >
            Drag & Drop Image
          </div>

          <div
            style={{
              fontSize: "12px",
              color: "#777"
            }}
          >
            or click to select image
          </div>

        </div>
      )}


      {/* ================================= */}
      {/* UPLOADED SERVER IMAGE */}
      {/* ================================= */}

      {ImageUrl && (
        <div className="my-2">

          <div>
            <img
              src={ImageUrl}
              alt="Uploaded"
              className="
                border
                border-dark
              "
              style={{
                maxWidth: "250px",
                maxHeight: "200px",
                objectFit: "contain",
                borderRadius: "5px"
              }}
            />
          </div>


          {/* ACTION BUTTONS */}

          <div
            className="
              d-flex
              align-items-center
              gap-3
              mt-2
            "
          >

            {/* CHANGE */}

            <button
              type="button"
              className="
                btn
                btn-link
                p-0
              "
              onClick={() => {
                fileRefSingle.current?.click();
              }}
            >
              <i className="fa fa-image me-1" />

              Change
            </button>


            {/* CLEAR */}

            <button
              type="button"
              className="
                btn
                btn-link
                text-danger
                p-0
              "
              onClick={
                handleClear
              }
            >
              <i className="fa fa-trash me-1" />

              Clear
            </button>

          </div>

        </div>
      )}


      {/* ================================= */}
      {/* HIDDEN FILE INPUT */}
      {/* ================================= */}

      <input
        type="file"
        accept="image/*"
        ref={fileRefSingle}
        style={{
          display: "none"
        }}
        onChange={
          handleFileSelect
        }
      />


      {/* ================================= */}
      {/* CROP MODAL */}
      {/* ================================= */}

      {showCrop && localImageUrl && (

        <div
          style={{
            position: "fixed",
            inset: 0,

            background:
              "rgba(0,0,0,0.70)",

            zIndex: 99999,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            padding: "20px"
          }}
        >

          <div
            style={{
              width: "700px",
              maxWidth: "100%",

              maxHeight: "90vh",

              background: "#fff",

              borderRadius: "10px",

              padding: "20px",

              overflowY: "auto"
            }}
          >

            {/* HEADER */}

            <div
              className="
                d-flex
                justify-content-between
                align-items-center
                mb-3
              "
            >

              <h4 className="m-0">
                Crop Image
              </h4>

              <button
                type="button"
                className="btn-close"
                disabled={Loading}
                onClick={
                  cancelCrop
                }
              />

            </div>


            {/* INFORMATION */}

            <div
              className="
                alert
                alert-light
                py-2
              "
              style={{
                fontSize: "13px"
              }}
            >
              Drag the crop box to select the
              area you want to upload.
            </div>


            {/* ================================= */}
            {/* CROPPER */}
            {/* ================================= */}

            <div
              style={{
                width: "100%",
                textAlign: "center",
                background: "#f5f5f5",
                padding: "10px",
                borderRadius: "5px"
              }}
            >

              <ReactCrop
                crop={crop}

                onChange={(
                  pixelCrop,
                  percentCrop
                ) => {
                  /*
                   * Store percentage crop
                   * so crop remains stable
                   * if image display changes.
                   */
                  setCrop(
                    percentCrop
                  );
                }}

                onComplete={(
                  pixelCrop
                ) => {
                  /*
                   * Store pixel crop.
                   * This is used to create
                   * the final cropped image.
                   */
                  setCompletedCrop(
                    pixelCrop
                  );
                }}

                keepSelection
              >

                <img
                  ref={
                    cropImageRef
                  }

                  src={
                    localImageUrl
                  }

                  alt="Crop"

                  onLoad={(event) => {
                    /*
                     * Start with a crop
                     * covering 80% of image.
                     */
                    const image =
                      event.currentTarget;

                    const cropValue = {
                      unit: "%",
                      x: 0,
                      y: 0,
                      width: 100,
                      height: 100
                    };

                    setCrop(
                      cropValue
                    );

                    /*
                     * Calculate initial
                     * pixel crop too.
                     */
                    setCompletedCrop({
                      unit: "px",

                      x:
                        image.width *
                        0.10,

                      y:
                        image.height *
                        0.10,

                      width:
                        image.width *
                        0.80,

                      height:
                        image.height *
                        0.80
                    });
                  }}

                  style={{
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: "500px",
                    margin: "auto"
                  }}
                />

              </ReactCrop>

            </div>


            {/* ================================= */}
            {/* BUTTONS */}
            {/* ================================= */}

            <div
              className="
                d-flex
                flex-wrap
                justify-content-end
                gap-2
                mt-4
              "
            >

              {/* CANCEL */}

              <button
                type="button"
                className="
                  btn
                  btn-secondary
                "
                disabled={Loading}
                onClick={
                  cancelCrop
                }
              >
                Cancel
              </button>


              {/* ORIGINAL */}

              <button
                type="button"
                className="
                  btn
                  btn-outline-primary
                "
                disabled={Loading}
                onClick={
                  uploadOriginalImage
                }
              >
                {Loading
                  ? "Uploading..."
                  : "Upload Original"
                }
              </button>


              {/* CROP AND UPLOAD */}

              <button
                type="button"
                className="
                  btn
                  btn-primary
                "
                disabled={Loading}
                onClick={
                  uploadCroppedImage
                }
              >

                {Loading ? (
                  <>
                    <span
                      className="
                        spinner-border
                        spinner-border-sm
                        me-2
                      "
                    />

                    Uploading...
                  </>
                ) : (
                  <>
                    <i
                      className="
                        fa
                        fa-crop
                        me-2
                      "
                    />

                    Crop & Upload
                  </>
                )}

              </button>

            </div>

          </div>

        </div>
      )}

    </div>
  );
}